import axios, { AxiosError, AxiosRequestConfig, AxiosHeaders } from "axios";
import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getCsrfToken, requiresCsrfToken } from "./csrf";
import { captureSentryException, addSentryBreadcrumb } from "@/config/sentryConfig";

export class ApiError extends Error {
  code: number;
  data?: unknown;

  constructor(code: number, message: string, data?: unknown) {
    super(message);
    this.code = code;
    this.data = data;
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// Store logout callback to avoid circular dependency
type LogoutCallback = (options?: { remote?: boolean }) => Promise<void>;

let logoutCallback: LogoutCallback | null = null;
let redirectCallback: ((path: string) => void) | null = null;

export function setAuthCallbacks(
  logout: LogoutCallback,
  redirect: (path: string) => void
) {
  logoutCallback = logout;
  redirectCallback = redirect;
}

// Create a single Axios instance for the entire client
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: true,
});

// Allow requests to opt out of global 401 handling (for returnNull semantics)
type ApiRequestConfig = AxiosRequestConfig & { skipUnauthorizedHandler?: boolean };

// Request interceptor: attach CSRF token for state-changing methods
api.interceptors.request.use((config) => {
  const method = (config.method || "GET").toUpperCase();
  if (requiresCsrfToken(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      // Normalize to AxiosHeaders and set CSRF header safely
      const headers = AxiosHeaders.from(config.headers);
      headers.set("X-CSRF-Token", csrfToken);
      config.headers = headers;
    }
  }
  return config;
});

// Response interceptor: unified 401 handling and error tracking
api.interceptors.response.use(
  (response) => {
    // Add successful API calls as breadcrumbs
    addSentryBreadcrumb(`API ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      statusText: response.statusText,
    });
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const cfg = (error.config as ApiRequestConfig) || {};

    // Capture API errors to Sentry (exclude 401 as they're expected during logout)
    if (status && status !== 401) {
      captureSentryException(error, {
        url: error.config?.url,
        method: error.config?.method,
        status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
      });
    }

    if (status === 401 && !cfg.skipUnauthorizedHandler) {
      if (logoutCallback && redirectCallback) {
        await logoutCallback({ remote: false });
        redirectCallback('/');
      }
    }
    return Promise.reject(error);
  }
);

export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<T> {
  const methodUpper = method.toUpperCase();
  try {
    const config: ApiRequestConfig = {
      method: methodUpper,
      url,
      data,
    };
    // If sending FormData, set appropriate header to let Axios handle boundaries
    if (data instanceof FormData) {
      config.headers = { 'Content-Type': 'multipart/form-data' };
    }
    const response = await api.request<T>(config);
    return response.data as T;
  } catch (err: any) {
    const status = err?.response?.status;
    const message = err?.response?.data || err?.message || 'Request failed';
    // Re-throw a normalized error for callers expecting Error
    const e = new Error(`${status ?? 'Error'}: ${typeof message === 'string' ? message : JSON.stringify(message)}`) as Error & { status?: number };
    if (typeof status === 'number') e.status = status;
    throw e;
  }
}

export async function apiRequestV2<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<T> {
  const methodUpper = method.toUpperCase();
  const config: ApiRequestConfig = {
    method: methodUpper,
    url,
    data,
  };

  // If sending FormData, set appropriate header to let Axios handle boundaries
  if (data instanceof FormData) {
    config.headers = { 'Content-Type': 'multipart/form-data' };
  }
  try {
    const response = await api.request<T>(config);
    return response.data as T;
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const data = err?.response?.data;
    const message = typeof data === 'string' ? data : data?.message ?? err?.message ?? 'Request failed';
    throw new ApiError(status, message, data);
  }
}

export function parseNormalizedError(error: any): { message: string; details?: any; status?: number } {
  let message: string = error?.message || error?.response?.data?.message || 'Request failed';
  let details: any = error?.details || error?.data?.details || error?.response?.data?.details;
  const status: number | undefined = typeof error?.status === 'number'
    ? error.status
    : typeof error?.code === 'number'
      ? error.code
      : error?.response?.status;
  if (typeof message === 'string') {
    const s = message.indexOf('{');
    const e = message.lastIndexOf('}');
    if (s !== -1 && e !== -1 && e > s) {
      try {
        const parsed = JSON.parse(message.substring(s, e + 1));
        if (parsed?.message) message = parsed.message;
        if (parsed?.details) details = parsed.details;
      } catch {}
    }
  }
  return { message, details, status };
}

type UnauthorizedBehavior = "returnNull" | "throw";
export function getQueryFn<T>(options: { on401: UnauthorizedBehavior }): QueryFunction<T> {
  const { on401: unauthorizedBehavior } = options;
  return async ({ queryKey }) => {
    const [url, params] = queryKey as [string, unknown?];
    const finalUrl = url as string;

    try {
      const resp = await api.get<T>(finalUrl, {
        // If the caller wants to swallow 401s, skip global handler
        skipUnauthorizedHandler: unauthorizedBehavior === 'returnNull',
        params: (params && typeof params === 'object') ? (params as Record<string, any>) : undefined,
      } as ApiRequestConfig);
      return resp.data as T;
    } catch (err: any) {
      const status = err?.response?.status;
      if (unauthorizedBehavior === 'returnNull' && status === 401) {
        return null as T;
      }
      // For other errors, normalize and throw
      const message = err?.response?.data || err?.message || 'Request failed';
      const e = new Error(`${status ?? 'Error'}: ${typeof message === 'string' ? message : JSON.stringify(message)}`);
      throw e;
    }
  };
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
